import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, TextInput, Alert, Image, ActivityIndicator, ImageBackground, Share, DeviceEventEmitter, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Svg, Path, Rect, Circle, Defs, RadialGradient, Stop, G, Ellipse, ClipPath } from 'react-native-svg';
import * as ImagePicker from 'expo-image-picker';
import * as WebBrowser from 'expo-web-browser';
import Toast from '../components/Toast';
import Avatar from '../components/Avatar';
import AvatarBlurRing from '../components/AvatarBlurRing';
import PageHeader from '../components/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/lib/supabase';
import { fetchUserClinic, Clinic } from '../services/clinicsApi';
import { GalleryConsentService } from '../services/galleryConsentService';
import { ImageUploadService } from '../services/imageUploadService';
import { useServerVerifiedPlus } from '@/hooks/useEntitlement';
import { useOfferings } from '@/hooks/useOfferings';
import RevenueCatService from '@/services/revenueCatService';
import { PAYWALL_RESULT } from 'react-native-purchases-ui';
import { resolveAvatarUrl } from '@/services/profilePhotoCacheService';
import Constants from 'expo-constants';
import { Skeleton } from '@/components/Skeleton';
import { biometricService } from '@/services/biometricService';
import AppSwitch from '@/components/AppSwitch';

const ChevronRightIcon = () => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path d="M5.83325 3.33325L10.4999 7.99992L5.83325 12.6666" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
  </Svg>
);

export default function PerfilScreen() {
  const router = useRouter();

  // Always call useAuth hook first, regardless of conditions
  const { 
    user, 
    userProfile, 
    updateProfile, 
    refreshProfile, 
    error: authError,
    loading: authLoading,
    retryInitialization,
    clearError,
    signOut,
    sessionReady,
    session
  } = useAuth();

  // State hooks - always called after useAuth
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  const [profileLoading, setProfileLoading] = useState(true);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [imageLoading, setImageLoading] = useState(false);
  const [clinicData, setClinicData] = useState<Clinic | null>(null);
  const [isUploading, setIsUploading] = useState(false); // Added state for image upload
  const [showingPaywall, setShowingPaywall] = useState(false);
  const { hasPadrinhoPlus, isLoading: isCheckingEntitlement, refresh: refreshEntitlement } = useServerVerifiedPlus();
  const { getTitle, getCTA, metadata, isLoading: isLoadingOfferings } = useOfferings();
  const { language, t } = useLanguage();
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricIsFaceId, setBiometricIsFaceId] = useState(false);
  const [biometricToggleLoading, setBiometricToggleLoading] = useState(false);

  const getLanguageLabel = () => {
    switch (language) {
      case 'pt': return t('profile.languages.pt');
      case 'en': return t('profile.languages.en');
      case 'es': return t('profile.languages.es');
      default: return t('profile.languages.pt');
    }
  };

  // Check biometric availability on mount
  useEffect(() => {
    const checkBiometric = async () => {
      try {
        const [available, enabled] = await Promise.all([
          biometricService.isAvailable(),
          biometricService.isEnabled(),
        ]);
        if (available) {
          const isFace = await biometricService.hasFaceId();
          setBiometricAvailable(true);
          setBiometricIsFaceId(isFace);
          setBiometricEnabled(enabled);
        }
      } catch {
        // biometric not available — no-op
      }
    };
    checkBiometric();

    // PRD §5.10 — `settings_opened`. Fires on every mount of the profile
    // tab; Clarity dedupes per session so re-visits inside the same
    // session aren't double-counted.
    import('@/utils/clarityEvents')
      .then(({ logSettingsOpened }) => logSettingsOpened())
      .catch(() => {});
  }, []);

  const handleBiometricToggle = async (value: boolean) => {
    if (biometricToggleLoading) return;
    setBiometricToggleLoading(true);
    try {
      if (value) {
        const reason = t('biometric.authReason');
        const authenticated = await biometricService.authenticate(reason);
        if (!authenticated) {
          // PRD §5.1 — `auth_biometric_enrolled` (declined branch). User
          // dismissed the system prompt or the device-level auth failed;
          // the helper unifies enrolled/declined under one event with a
          // bounded `result` enum.
          import('@/utils/clarityEvents')
            .then(({ logAuthBiometricEnrolled }) =>
              logAuthBiometricEnrolled({ result: 'declined' })
            )
            .catch(() => {});
          return;
        }
        if (!session) return;
        await biometricService.storeSession(session);
        setBiometricEnabled(true);
        // PRD §5.1 — `auth_biometric_enrolled` (enrolled branch). Successful
        // enrolment via the settings toggle (the only place the user can
        // opt in post-onboarding).
        import('@/utils/clarityEvents')
          .then(({ logAuthBiometricEnrolled }) =>
            logAuthBiometricEnrolled({ result: 'enrolled' })
          )
          .catch(() => {});
      } else {
        Alert.alert(
          t('biometric.disableConfirmTitle'),
          t('biometric.disableConfirmMessage'),
          [
            { text: t('biometric.disableConfirmNo'), style: 'cancel' },
            {
              text: t('biometric.disableConfirmYes'),
              style: 'destructive',
              onPress: async () => {
                await biometricService.disable();
                setBiometricEnabled(false);
              },
            },
          ]
        );
      }
    } catch {
      // no-op
    } finally {
      setBiometricToggleLoading(false);
    }
  };

  // Update tempName when profile changes
  useEffect(() => {
    if (userProfile?.full_name && !isEditingName) {
      setTempName(userProfile.full_name);
    }
  }, [userProfile?.full_name, isEditingName]);

  // Handle profile loading state
  useEffect(() => {
    if (authLoading) {
      setProfileLoading(true);
    } else {
      // Add small delay to prevent flash
      const timer = setTimeout(() => {
        setProfileLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [authLoading]);

  // Listen for subscription status changes (from RevenueCat sync)
  useEffect(() => {
    const subscription = DeviceEventEmitter.addListener(
      'subscription:status-changed',
      async (data: { hasPadrinhoPlus: boolean }) => {
        console.log('💎 Profile: Subscription status changed:', data.hasPadrinhoPlus);
        // Refresh entitlement state to update UI
        await refreshEntitlement();
      }
    );

    return () => subscription.remove();
  }, [refreshEntitlement]);

  // Load clinic data - wait for session to be ready
  useEffect(() => {
    const loadClinicData = async () => {
      if (!userProfile?.id || !sessionReady || !session?.access_token) return;

      try {
        console.log('🏥 Loading clinic data for profile page...');
        const data = await fetchUserClinic(session.access_token);
        if (data) {
          console.log('🏥 Clinic data loaded:', {
            name: data.name,
            id: data.id
          });
          setClinicData(data);
        } else {
          console.log('🏥 No clinic found for user');
          setClinicData({ name: 'Padrinho', id: 'default', is_active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }); // Fallback
        }
      } catch (error) {
        console.error("🏥 Failed to fetch clinic data:", error);
        setClinicData({ name: 'Padrinho', id: 'default', is_active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }); // Fallback
      }
    };

    loadClinicData();
    // C3 (PV2-16): drop access_token from deps — rotates on auto-refresh.
  }, [userProfile?.id, sessionReady]);

  const handleClose = () => {
    router.back();
  };

  const handleImagePicker = async () => {
    // Prevent multiple simultaneous calls
    if (imageLoading) {
      console.log('⚠️ Image picker already in progress');
      return;
    }

    try {
      console.log('📷 Starting image picker flow...');
      setImageLoading(true);

      // Add small delay to ensure UI state updates before permissions
      await new Promise(resolve => setTimeout(resolve, 100));

      // Use system default permission flow - no custom prompts
      const imageUri = await GalleryConsentService.requestPermissionAndPickImage();

      // Check if component is still mounted and user hasn't navigated away
      if (!user?.id) {
        console.log('❌ User not available after permission request');
        showToast(t('profile.toasts.authError'), 'error');
        return;
      }

      if (!imageUri) {
        console.log('❌ No image selected or permission denied');
        showToast(t('profile.toasts.noImageSelected'), 'info');
        return;
      }

      console.log('📷 Image selected for upload:', imageUri);

      // Validate selected image
      const validation = ImageUploadService.validateImageFile(imageUri);
      if (!validation.isValid) {
        console.log('❌ Invalid image file:', validation.error);
        showToast(validation.error || t('profile.toasts.invalidFile'), 'error');
        return;
      }

      // Upload the image with error handling
      showToast(t('profile.toasts.uploadingPhoto'), 'info');
      const result = await ImageUploadService.uploadProfilePicture(user.id, imageUri);

      if (result.error) {
        console.error('❌ Upload failed:', result.error);
        showToast(t('profile.toasts.uploadFailed'), 'error');
        return;
      }

      if (result.url) {
        console.log('✅ Profile picture uploaded successfully:', result.url);

        // Clean up old avatar (don't wait for this)
        if (userProfile?.avatar_url) {
          console.log('🗑️ Cleaning up old avatar...');
          ImageUploadService.cleanupOldAvatar(userProfile.avatar_url).catch((error) => {
            console.warn('⚠️ Failed to cleanup old avatar:', error);
          });
        }

        // Refresh the profile to propagate changes throughout the app
        if (refreshProfile) {
          try {
            await refreshProfile();
          } catch (refreshError) {
            console.warn('⚠️ Failed to refresh profile:', refreshError);
          }
        }

        showToast(t('profile.toasts.photoUpdated'), 'success');
      }
    } catch (error) {
      console.error('❌ Error in handleImagePicker:', error);

      if (error instanceof Error) {
        if (error.message.includes('permission')) {
          showToast(t('profile.toasts.permissionRequired'), 'error');
        } else if (error.message.includes('network') || error.message.includes('upload')) {
          showToast(t('profile.toasts.connectionError'), 'error');
        } else if (error.message.includes('User rejected permissions')) {
          showToast(t('profile.toasts.permissionDenied'), 'info');
        } else {
          showToast(t('profile.toasts.photoUpdateError'), 'error');
        }
      } else {
        showToast(t('profile.toasts.unexpectedError'), 'error');
      }
    } finally {
      console.log('🏁 Image picker flow completed');
      setImageLoading(false);
    }
  };


  const handleNameEdit = () => {
    setIsEditingName(true);
    setTempName(userProfile?.full_name || '');
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  const handleNameSave = async () => {
    if (tempName.trim()) {
      const { error } = await updateProfile({ full_name: tempName.trim() });
      if (error) {
        showToast(t('profile.toasts.nameUpdateError'), 'error');
      } else {
        setIsEditingName(false);
        showToast(t('profile.toasts.nameSaved'), 'success');
      }
    }
  };

  const handleNameCancel = () => {
    setTempName(userProfile?.full_name || '');
    setIsEditingName(false);
  };

  const handleLogout = async () => {
    Alert.alert(
      t('profile.logout.title'),
      t('profile.logout.message'),
      [
        {
          text: t('profile.logout.cancel'),
          style: 'cancel',
        },
        {
          text: t('profile.logout.confirm'),
          style: 'destructive',
          onPress: async () => {
            try {
              console.log('🚪 User confirmed logout');
              await signOut({ keepBiometric: true });

              console.log('✅ Logout successful, redirecting...');

              // Small delay to ensure state is cleared
              setTimeout(() => {
                router.replace('/welcome');
              }, 100);

            } catch (error) {
              console.error('❌ Unexpected logout error:', error);
              showToast(t('profile.toasts.logoutError'), 'error');
            }
          },
        },
      ]
    );
  };

  const handleUpgradeToPlus = async () => {
    if (showingPaywall) return;
    
    try {
      setShowingPaywall(true);
      console.log('💰 Showing RevenueCat paywall from profile...');
      
      const { result } = await RevenueCatService.presentPaywall({ fromSurface: 'profile' });
      
      if (result === PAYWALL_RESULT.PURCHASED || result === PAYWALL_RESULT.RESTORED) {
        console.log('✅ Purchase successful or restored');
        await refreshEntitlement();
        showToast(t('profile.toasts.plusUpgradeSuccess'), 'success');
      } else if (result === PAYWALL_RESULT.CANCELLED) {
        console.log('ℹ️ User cancelled paywall');
      }
    } catch (error) {
      console.error('❌ Error showing paywall:', error);
      showToast(t('profile.toasts.paywallError'), 'error');
    } finally {
      setShowingPaywall(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      console.log('💰 Showing RevenueCat customer center from profile...');
      await RevenueCatService.presentCustomerCenter();
      console.log('✅ Customer center closed');
      
      // Refresh entitlement in case user made changes
      await refreshEntitlement();
    } catch (error) {
      console.error('❌ Error showing customer center:', error);
      showToast(t('profile.toasts.subscriptionManagementError'), 'error');
    }
  };

  // Helper function to get first name
  const getFirstName = () => {
    if (userProfile?.full_name) {
      return userProfile.full_name.split(' ')[0];
    }
    return t('profile.defaults.userName');
  };

  // Render logic with proper loading states
  if (authLoading || profileLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
              <Ionicons name="close" size={24} color="#151515" />
            </TouchableOpacity>
          </View>
        </View>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>{t('common.loading')}</Text>
        </View>
      </SafeAreaView>
    );
  }

  // Show error state with recovery options
  if (authError) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
              <Ionicons name="close" size={24} color="#151515" />
            </TouchableOpacity>
          </View>
        </View>
        <View style={[styles.container, styles.centerContent]}>
          <Text style={styles.errorTitle}>{t('profile.errors.loadProfile')}</Text>
          <Text style={styles.errorText}>{authError}</Text>
          <View style={styles.errorActions}>
            <TouchableOpacity 
              style={styles.retryButton}
              onPress={retryInitialization}
            >
              <Text style={styles.retryButtonText}>{t('profile.errors.retry')}</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.clearErrorButton}
              onPress={() => {
                clearError();
                router.push('/welcome');
              }}
            >
              <Text style={styles.clearErrorButtonText}>{t('profile.errors.backToLogin')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  if (!user) {
    return (
      <SafeAreaView style={styles.container}>
        <PageHeader 
          title={t('profile.title')} 
          onBack={() => router.back()}
          backgroundColor="#F9F8F3"
        />
        <View style={[styles.container, styles.centerContent]}>
          <Text style={styles.errorTitle}>{t('profile.errors.userNotFound')}</Text>
          <Text style={styles.errorText}>
            {t('profile.errors.userDataError')}
          </Text>
          <TouchableOpacity 
            style={styles.retryButton}
            onPress={() => router.push('/welcome')}
          >
            <Text style={styles.retryButtonText}>{t('profile.errors.makeLogin')}</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <PageHeader 
        title={t('profile.title')} 
        onBack={() => router.back()}
        backgroundColor="#F9F8F3"
      />

      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        {/* Profile Section with Avatar and Greeting */}
        <View style={styles.profileHeaderSection}>
          <View style={styles.avatarContainer}>
            <TouchableOpacity 
              onPress={handleImagePicker} 
              activeOpacity={0.8}
              disabled={imageLoading}
              style={styles.avatarTouchable}
            >
              {hasPadrinhoPlus ? (
                <AvatarBlurRing size={88}>
                  <Avatar
                    uri={resolveAvatarUrl(userProfile?.avatar_url)}
                    size={88}
                    loading={imageLoading}
                    showInitials={true}
                    initials={userProfile?.full_name || undefined}
                    borderWidth={0}
                  />
                </AvatarBlurRing>
              ) : (
                <Avatar
                  uri={resolveAvatarUrl(userProfile?.avatar_url)}
                  size={88}
                  loading={imageLoading}
                  showInitials={true}
                  initials={userProfile?.full_name || undefined}
                  borderColor="#7E7D75"
                  borderWidth={1}
                />
              )}
            </TouchableOpacity>
            {hasPadrinhoPlus && (
              <View style={styles.plusBadge}>
                <Svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <Path d="M1.75524 5.7637L5.40815 9.7771C5.72535 10.1259 6.2741 10.1259 6.59125 9.7771L10.2447 5.7632C10.5502 5.42805 10.5852 4.92698 10.3298 4.55191L8.8587 2.39099C8.6763 2.12343 8.37275 1.96289 8.04875 1.96289H3.95555C3.63156 1.96289 3.32849 2.12294 3.14606 2.3905L1.67059 4.55191C1.4147 4.92649 1.44973 5.42805 1.75524 5.7637Z" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M6.99847 1.96289L7.68197 4.55191C7.78117 4.92698 7.76757 5.42805 7.64887 5.7632L6.22887 9.7771C6.10577 10.1259 5.89222 10.1259 5.76912 9.7771L4.34912 5.7637C4.23042 5.42805 4.2168 4.92649 4.31653 4.55191L5.00197 1.96289" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M1.50391 5.10156H10.4954" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
              </View>
            )}
            <TouchableOpacity 
              style={[styles.editButton, imageLoading && styles.editButtonDisabled]} 
              onPress={handleImagePicker}
              disabled={imageLoading}
            >
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path fillRule="evenodd" clipRule="evenodd" d="M10.0254 2.701C10.6987 2.969 10.9047 3.90233 11.18 4.20233C11.4554 4.50233 11.8494 4.60433 12.0674 4.60433C13.226 4.60433 14.1654 5.54367 14.1654 6.70167V10.565C14.1654 12.1183 12.9054 13.3783 11.352 13.3783H4.64536C3.09136 13.3783 1.83203 12.1183 1.83203 10.565V6.70167C1.83203 5.54367 2.77136 4.60433 3.93003 4.60433C4.14736 4.60433 4.54136 4.50233 4.81736 4.20233C5.0927 3.90233 5.29803 2.969 5.97136 2.701C6.64536 2.433 9.35203 2.433 10.0254 2.701Z" stroke="#F0EEE4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M11.6641 6.3335H11.6701" stroke="#F0EEE4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <Path fillRule="evenodd" clipRule="evenodd" d="M10.1176 8.75215C10.1176 7.58148 9.16891 6.63281 7.99824 6.63281C6.82757 6.63281 5.87891 7.58148 5.87891 8.75215C5.87891 9.92281 6.82757 10.8715 7.99824 10.8715C9.16891 10.8715 10.1176 9.92281 10.1176 8.75215Z" stroke="#F0EEE4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
            </TouchableOpacity>
          </View>
          <View style={styles.greetingTextContainer}>
            <Text>
              <Text style={styles.greetingHello}>{t('profile.greeting.hello')}</Text>
              <Text style={styles.greetingName}>{getFirstName()}{t('profile.greeting.wave')}</Text>
            </Text>
          </View>
        </View>

        {/* Padrinho Plus & CSAT Cards Section */}
        <View style={styles.cardsSection}>
          {/* Padrinho Plus Upgrade Card - Only show for non-Plus users */}
          {!hasPadrinhoPlus && !isCheckingEntitlement && (
            <TouchableOpacity 
              style={styles.upgradeCard} 
              onPress={handleUpgradeToPlus}
              disabled={showingPaywall}
              activeOpacity={0.8}
            >
              <View style={styles.cardBackgroundContainer}>
                <Svg width="100%" height="100%" viewBox="0 0 469 207" preserveAspectRatio="xMidYMid slice">
                  <Defs>
                    <RadialGradient id="profileGlow1" cx="50%" cy="50%" rx="50%" ry="50%">
                      <Stop offset="0%" stopColor="white" stopOpacity="0.9" />
                      <Stop offset="40%" stopColor="white" stopOpacity="0.4" />
                      <Stop offset="100%" stopColor="white" stopOpacity="0" />
                    </RadialGradient>
                    <RadialGradient id="profileGlow2" cx="50%" cy="50%" rx="50%" ry="50%">
                      <Stop offset="0%" stopColor="#015480" stopOpacity="1" />
                      <Stop offset="50%" stopColor="#015480" stopOpacity="0.5" />
                      <Stop offset="100%" stopColor="#015480" stopOpacity="0" />
                    </RadialGradient>
                    <RadialGradient id="profileGlow3" cx="50%" cy="50%" rx="50%" ry="50%">
                      <Stop offset="0%" stopColor="#015480" stopOpacity="0.9" />
                      <Stop offset="50%" stopColor="#015480" stopOpacity="0.4" />
                      <Stop offset="100%" stopColor="#015480" stopOpacity="0" />
                    </RadialGradient>
                  </Defs>
                  <G opacity={0.9}>
                    <Ellipse cx="60" cy="120" rx="140" ry="130" fill="url(#profileGlow1)" />
                  </G>
                  <Ellipse cx="300" cy="140" rx="150" ry="140" fill="url(#profileGlow2)" />
                  <Ellipse cx="420" cy="40" rx="100" ry="100" fill="url(#profileGlow3)" />
                </Svg>
              </View>
              <View style={styles.upgradeIcon}>
                <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <Path d="M2.92539 9.60617L9.01358 16.2952C9.54225 16.8765 10.4568 16.8765 10.9854 16.2952L17.0745 9.60533C17.5837 9.04675 17.642 8.21163 17.2163 7.58651L14.7645 3.98498C14.4605 3.53904 13.9546 3.27148 13.4146 3.27148H6.59259C6.0526 3.27148 5.54748 3.53823 5.24343 3.98417L2.78431 7.58651C2.35783 8.21082 2.41622 9.04675 2.92539 9.60617Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M11.6667 3.27148L12.8059 7.58651C12.9712 8.21163 12.9486 9.04675 12.7507 9.60533L10.3841 16.2952C10.1789 16.8765 9.82297 16.8765 9.61781 16.2952L7.25114 9.60617C7.05331 9.04675 7.03061 8.21082 7.19682 7.58651L8.33923 3.27148" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M2.50781 8.50244H17.4936" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.upgradeContent}>
                <Text style={styles.upgradeTitle}>{t('profile.upgrade.title')}</Text>
                <Text style={styles.upgradeSubtitle}>{t('profile.upgrade.subtitle')}</Text>
              </View>
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path d="M5.83203 3.3335L10.4987 8.00016L5.83203 12.6668" stroke="white" strokeWidth="2" strokeLinecap="square"/>
              </Svg>
            </TouchableOpacity>
          )}

          {/* CSAT Research Card */}
          <TouchableOpacity 
            style={styles.csatCard} 
            onPress={() => router.push('/csat')}
            activeOpacity={0.8}
          >
            <View style={styles.csatIcon}>
              <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <Path fillRule="evenodd" clipRule="evenodd" d="M10.9202 3.48068L12.4431 6.52297C12.5923 6.82163 12.8804 7.02873 13.2145 7.07666L16.6211 7.56702C17.4628 7.68854 17.7977 8.70863 17.1886 9.29312L14.7252 11.6602C14.4831 11.893 14.3729 12.2276 14.4301 12.5562L15.0115 15.898C15.1547 16.7247 14.2748 17.3554 13.5225 16.9643L10.4777 15.3854C10.1792 15.2305 9.82168 15.2305 9.52232 15.3854L6.4775 16.9643C5.72519 17.3554 4.84533 16.7247 4.98937 15.898L5.56987 12.5562C5.62714 12.2276 5.51694 11.893 5.27485 11.6602L2.8114 9.29312C2.20226 8.70863 2.5372 7.68854 3.37889 7.56702L6.78554 7.07666C7.11961 7.02873 7.40856 6.82163 7.55781 6.52297L9.07979 3.48068C9.45638 2.72845 10.5436 2.72845 10.9202 3.48068Z" stroke="#F0EEE4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
            </View>
            <View style={styles.csatContent}>
              <Text style={styles.csatTitle}>{t('profile.csat.title')}</Text>
              <Text style={styles.csatSubtitle}>{t('profile.csat.subtitle')}</Text>
            </View>
          </TouchableOpacity>
        </View>

        {/* Sponsor Matching Section - Padrinho Plus Feature */}
        {/* SUAS INFORMAÇÕES Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitleNew}>{t('profile.sections.yourInfo')}</Text>
          <View style={styles.infoCard}>
            {/* Apelido */}
            <TouchableOpacity 
              style={styles.infoItem} 
              onPress={() => router.push('/change-name')}
            >
              <View style={styles.infoItemIcon}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <Path fillRule="evenodd" clipRule="evenodd" d="M11.983 15.3462C8.11536 15.3462 4.8125 15.931 4.8125 18.2729C4.8125 20.6148 8.0944 21.2205 11.983 21.2205C15.8506 21.2205 19.1525 20.6348 19.1525 18.2938C19.1525 15.9529 15.8715 15.3462 11.983 15.3462Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path fillRule="evenodd" clipRule="evenodd" d="M11.983 12.0059C14.5211 12.0059 16.5782 9.94779 16.5782 7.40969C16.5782 4.8716 14.5211 2.81445 11.983 2.81445C9.44487 2.81445 7.38675 4.8716 7.38675 7.40969C7.3782 9.93922 9.42201 11.9973 11.9506 12.0059H11.983Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('profile.fields.nickname')}</Text>
                <Text style={styles.infoItemSubtitle}>{userProfile?.full_name || t('profile.fields.notInformed')}</Text>
              </View>
              <ChevronRightIcon />
            </TouchableOpacity>

            {/* Nome de usuário */}
            <TouchableOpacity
              style={styles.infoItem}
              onPress={() => router.push('/change-username')}
            >
              <View style={styles.infoItemIcon}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <Path d="M15.6 8.6413V12.899C15.6 14.3906 16.8084 15.599 18.3 15.599C19.9589 15.599 21 14.2369 21 12C21 6.93859 16.9699 3 11.999 3C7.02908 3 3 7.03005 3 12.001" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M17.0735 19.4336C15.5819 20.4523 13.814 21.0001 12.0004 21.0001C11.2598 21.0001 10.5401 20.9107 9.85156 20.742" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path fillRule="evenodd" clipRule="evenodd" d="M11.9984 8.39941C13.9862 8.39941 15.5984 10.0116 15.5984 11.9994C15.5984 13.9882 13.9862 15.5994 11.9984 15.5994C10.0106 15.5994 8.39844 13.9882 8.39844 11.9994C8.39844 10.0116 10.0106 8.39941 11.9984 8.39941Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M8.01957 17.5538H3M5.5094 20.0635V15.0439" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('profile.fields.username')}</Text>
                <Text style={styles.infoItemSubtitle}>
                  {userProfile?.username ? `@${userProfile.username}` : t('profile.fields.notInformed')}
                </Text>
              </View>
              <ChevronRightIcon />
            </TouchableOpacity>

            {/* E-mail */}
            <TouchableOpacity style={styles.infoItem} onPress={() => router.push('/change-email')}>
              <View style={styles.infoItemIcon}>
                <Svg width="24" height="20" viewBox="0 0 24 20" fill="none">
                  <Path d="M17.9028 6.85115L13.4596 10.4642C12.6201 11.1302 11.4389 11.1302 10.5994 10.4642L6.11865 6.85115" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path fillRule="evenodd" clipRule="evenodd" d="M16.9089 19C19.9502 19.0084 22 16.5095 22 13.4384V6.57001C22 3.49883 19.9502 1 16.9089 1H7.09114C4.04979 1 2 3.49883 2 6.57001V13.4384C2 16.5095 4.04979 19.0084 7.09114 19H16.9089Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('profile.fields.email')}</Text>
                <Text style={styles.infoItemSubtitle}>{userProfile?.email || t('profile.fields.notInformed')}</Text>
              </View>
              <ChevronRightIcon />
            </TouchableOpacity>

            {/* Telefone */}
            <TouchableOpacity style={styles.infoItem} onPress={() => router.push('/change-phone')}>
              <View style={styles.infoItemIcon}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <Path d="M18.4487 6.16543L18.4498 17.8335C18.4498 19.9999 16.6937 21.7559 14.5274 21.7559H8.54937C6.38408 21.7559 4.62802 19.9999 4.62802 17.8346L4.62695 6.1665C4.62695 4.00015 6.38302 2.24408 8.54832 2.24408H14.5274C16.6926 2.24408 18.4487 4.00015 18.4487 6.16543Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M10.0312 6.24234H13.0488" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M11.5388 17.7783V17.7261M11.5388 17.4517C11.389 17.4517 11.2676 17.5731 11.2676 17.7225C11.2676 17.8723 11.389 17.9937 11.5388 17.9937C11.6887 17.9937 11.8101 17.8723 11.8101 17.7225C11.8101 17.5731 11.6887 17.4517 11.5388 17.4517Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('profile.fields.phone')}</Text>
                <Text style={styles.infoItemSubtitle}>{userProfile?.phone || t('profile.fields.notInformed')}</Text>
              </View>
              <ChevronRightIcon />
            </TouchableOpacity>
          </View>
        </View>



        {/* ASSINATURA Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitleNew}>{t('profile.sections.subscription')}</Text>
          <View style={styles.infoCard}>
            {/* My Plan */}
            <TouchableOpacity style={styles.infoItem} onPress={() => router.push('/subscription')}>
              <View style={styles.infoItemIcon}>
                <Svg width="24" height="24" viewBox="0 0 20 20" fill="none">
                  <Path d="M2.92539 9.60617L9.01358 16.2952C9.54225 16.8765 10.4568 16.8765 10.9854 16.2952L17.0745 9.60533C17.5837 9.04675 17.642 8.21163 17.2163 7.58651L14.7645 3.98498C14.4605 3.53904 13.9546 3.27148 13.4146 3.27148H6.59259C6.0526 3.27148 5.54748 3.53823 5.24343 3.98417L2.78431 7.58651C2.35783 8.21082 2.41622 9.04675 2.92539 9.60617Z" stroke="#A19F97" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M11.6667 3.27148L12.8059 7.58651C12.9712 8.21163 12.9486 9.04675 12.7507 9.60533L10.3841 16.2952C10.1789 16.8765 9.82297 16.8765 9.61781 16.2952L7.25114 9.60617C7.05331 9.04675 7.03061 8.21082 7.19682 7.58651L8.33923 3.27148" stroke="#A19F97" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M2.50781 8.50244H17.4936" stroke="#A19F97" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('profile.fields.myPlan')}</Text>
                {isCheckingEntitlement ? (
                  <ActivityIndicator size="small" color="#015480" />
                ) : (
                  <Text style={styles.infoItemSubtitle}>{hasPadrinhoPlus ? getTitle() : t('profile.fields.basePlan')}</Text>
                )}
              </View>
              <ChevronRightIcon />
            </TouchableOpacity>

            {/* Provider */}
            <TouchableOpacity style={styles.infoItem} onPress={() => router.push('/provider')}>
              <View style={styles.infoItemIcon}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <Path d="M5.49021 7.18998C5.49021 5.97527 6.47443 4.99106 7.68913 4.99009H8.68988C9.27049 4.99009 9.82679 4.75959 10.2391 4.35112L10.9384 3.65089C11.7952 2.78922 13.1879 2.78533 14.0496 3.64117L14.0505 3.64214L14.0593 3.64992L14.7595 4.35015C15.1709 4.75959 15.7282 4.98911 16.3088 4.98911H17.3115C18.5262 4.98911 19.5114 5.9743 19.5114 7.18901V8.18878C19.5114 8.77036 19.7409 9.32666 20.1503 9.73902L20.8506 10.4392C21.7122 11.2961 21.7171 12.6887 20.8613 13.5504L20.1513 14.2604C19.7419 14.6718 19.5124 15.229 19.5124 15.8087V16.8123C19.5104 18.027 18.5252 19.0093 17.3115 19.0083H16.3068C15.7262 19.0083 15.169 19.2388 14.7576 19.6483L14.0574 20.3475C13.2025 21.2102 11.8098 21.216 10.9481 20.3611L10.9452 20.3592L10.2372 19.6512C9.82581 19.2417 9.26855 19.0122 8.68794 19.0113H7.68913C6.47443 19.0113 5.49021 18.027 5.49021 16.8123V15.8067C5.49021 15.2261 5.25972 14.6698 4.85028 14.2584L4.15102 13.5582C3.28837 12.7033 3.28254 11.3126 4.13643 10.4499C4.13643 10.4499 4.13935 10.448 4.14032 10.4461L4.84833 9.73707C5.25777 9.32569 5.48827 8.76842 5.48827 8.18684V7.18998" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M9.50391 12.1222L11.4811 14.1023L15.5541 10.0273" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('profile.fields.provider')}</Text>
                <Text style={styles.infoItemSubtitle}>{clinicData?.name || 'Padrinho'}</Text>
              </View>
              <ChevronRightIcon />
            </TouchableOpacity>
          </View>
        </View>

        {/* SEGURANÇA Section - Only Alterar senha */}
        <View style={styles.section}>
          <Text style={styles.sectionTitleNew}>{t('profile.sections.security')}</Text>
          <View style={styles.infoCard}>
            <TouchableOpacity style={styles.infoItem} onPress={() => router.push('/change-password')}>
              <View style={styles.infoItemIcon}>
                <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <Path fillRule="evenodd" clipRule="evenodd" d="M14.334 0.750099H5.665C2.644 0.750099 0.75 2.8891 0.75 5.9161V14.0841C0.75 17.1111 2.635 19.2501 5.665 19.2501H14.333C17.364 19.2501 19.25 17.1111 19.25 14.0841V5.9161C19.25 2.8891 17.364 0.750099 14.334 0.750099Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path fillRule="evenodd" clipRule="evenodd" d="M8.68886 10.0001C8.68886 11.0231 7.85986 11.8521 6.83686 11.8521C5.81386 11.8521 4.98486 11.0231 4.98486 10.0001C4.98486 8.9771 5.81386 8.1481 6.83686 8.1481H6.83986C7.86086 8.1491 8.68886 8.9781 8.68886 10.0001Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M8.69189 10.0001H15.0099V11.8521" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M12.1816 11.8522V10.0002" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('profile.fields.changePassword')}</Text>
              </View>
              <ChevronRightIcon />
            </TouchableOpacity>

            <>
              <View style={styles.separator} />
              <View style={[styles.infoItem, !biometricAvailable && styles.infoItemDisabled]}>
                <View style={styles.infoItemIcon}>
                  <Ionicons
                    name={biometricIsFaceId ? 'scan-outline' : 'finger-print-outline'}
                    size={22}
                    color="#A19F97"
                  />
                </View>
                <View style={styles.infoItemContent}>
                  <Text style={[styles.infoItemTitle, !biometricAvailable && styles.infoItemTitleDisabled]}>
                    {biometricIsFaceId ? t('biometric.settingTitle') : t('biometric.settingTitleFingerprint')}
                  </Text>
                  <Text style={styles.infoItemSubtitle}>
                    {biometricAvailable ? t('biometric.settingSubtitle') : t('biometric.unavailable')}
                  </Text>
                </View>
                <AppSwitch
                  value={biometricEnabled}
                  onValueChange={handleBiometricToggle}
                  disabled={!biometricAvailable || biometricToggleLoading}
                />
              </View>
            </>
          </View>
        </View>

        {/* APLICATIVO Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitleNew}>{t('profile.sections.app')}</Text>
          <View style={styles.infoCard}>
            <TouchableOpacity style={styles.infoItem} onPress={() => router.push('/interests-settings')}>
              <View style={styles.infoItemIcon}>
                <Svg width="20" height="16" viewBox="0 0 20 16" fill="none">
                  <Path fillRule="evenodd" clipRule="evenodd" d="M13.1609 8.05311C13.1609 9.79911 11.7449 11.2141 9.99889 11.2141C8.25289 11.2141 6.83789 9.79911 6.83789 8.05311C6.83789 6.30611 8.25289 4.89111 9.99889 4.89111C11.7449 4.89111 13.1609 6.30611 13.1609 8.05311Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path fillRule="evenodd" clipRule="evenodd" d="M9.998 15.355C13.806 15.355 17.289 12.617 19.25 8.05298C17.289 3.48898 13.806 0.750977 9.998 0.750977H10.002C6.194 0.750977 2.711 3.48898 0.75 8.05298C2.711 12.617 6.194 15.355 10.002 15.355H9.998Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('profile.fields.myInterests')}</Text>
                <Text style={styles.infoItemSubtitle}>{t('profile.fields.myInterestsSubtitle')}</Text>
              </View>
              <ChevronRightIcon />
            </TouchableOpacity>

            <TouchableOpacity style={styles.infoItem} onPress={() => router.push('/language-settings')}>
              <View style={styles.infoItemIcon}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <Path fillRule="evenodd" clipRule="evenodd" d="M12 3C16.9709 3 21 7.02908 21 12C21 16.9709 16.9709 21 12 21C7.02908 21 3 16.9709 3 12C3 7.02908 7.02908 3 12 3Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path fillRule="evenodd" clipRule="evenodd" d="M11.9984 3C13.849 3 15.3483 7.02908 15.3483 12C15.3483 16.9709 13.849 21 11.9984 21C10.1478 21 8.64844 16.9709 8.64844 12C8.64844 7.02908 10.1478 3 11.9984 3Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M3 11.998H21" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('profile.fields.language')}</Text>
                <Text style={styles.infoItemSubtitle}>{getLanguageLabel()}</Text>
              </View>
              <ChevronRightIcon />
            </TouchableOpacity>

            <TouchableOpacity style={styles.infoItem} onPress={() => router.push('/widget-preview')}>
              <View style={styles.infoItemIcon}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <Path fillRule="evenodd" clipRule="evenodd" d="M5.198 14H7.802C9.016 14 10 14.984 10 16.198V18.802C10 20.016 9.016 21 7.802 21H5.198C3.984 21 3 20.016 3 18.802V16.198C3 14.984 3.984 14 5.198 14Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path fillRule="evenodd" clipRule="evenodd" d="M16.198 14H18.802C20.016 14 21 14.984 21 16.198V18.802C21 20.016 20.016 21 18.802 21H16.198C14.984 21 14 20.016 14 18.802V16.198C14 14.984 14.984 14 16.198 14Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path fillRule="evenodd" clipRule="evenodd" d="M5.198 3H7.802C9.016 3 10 3.984 10 5.198V7.802C10 9.016 9.016 10 7.802 10H5.198C3.984 10 3 9.016 3 7.802V5.198C3 3.984 3.984 3 5.198 3Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M17.5051 9.99999L17.3691 9.63193C16.8562 8.24404 15.7636 7.14976 14.3777 6.6362L14.0102 6.5L14.3777 6.3638C15.7636 5.85024 16.8562 4.75598 17.3691 3.36809L17.5051 2.99998L17.6411 3.36809C18.1539 4.75598 19.2465 5.85024 20.6324 6.3638L21 6.5L20.6324 6.6362C19.2465 7.14976 18.1539 8.24404 17.6411 9.63193L17.5051 9.99999Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('profile.fields.widget')}</Text>
                <Text style={styles.infoItemSubtitle}>{t('profile.fields.widgetSubtitle')}</Text>
              </View>
              <ChevronRightIcon />
            </TouchableOpacity>
          </View>
        </View>

        {/* PADRINHO Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitleNew}>Padrinho</Text>
          <View style={styles.infoCard}>
            {/* Instagram */}
            <TouchableOpacity 
              style={styles.infoItem}
              onPress={() => WebBrowser.openBrowserAsync('https://www.instagram.com/padrinho.app/', {
                dismissButtonStyle: 'close',
                presentationStyle: WebBrowser.WebBrowserPresentationStyle.CURRENT_CONTEXT,
                controlsColor: '#7E7D75',
                toolbarColor: '#FFFFFF',
              })}
            >
              <View style={styles.infoItemIcon}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <Path d="M7.78216 3H16.2169C19.165 3 21 5.08119 21 8.02638V15.9736C21 18.9188 19.165 21 16.2159 21H7.78216C4.83405 21 3 18.918 3 15.9736V8.02638C3 5.08119 4.84281 3 7.78216 3Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M11.8445 8.82812C9.91023 8.82812 8.3418 10.3966 8.3418 12.3308C8.3418 14.2651 9.91023 15.8335 11.8445 15.8335C13.7788 15.8335 15.3472 14.2651 15.3472 12.3308C15.3472 10.3966 13.7788 8.82812 11.8445 8.82812Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M16.8661 7.36374V7.43274M17.1481 7.37875C17.1481 7.53575 17.0211 7.66275 16.8641 7.66275C16.7071 7.66275 16.5791 7.53575 16.5791 7.37875C16.5791 7.22175 16.7071 7.09375 16.8641 7.09375C17.0211 7.09375 17.1481 7.22175 17.1481 7.37875Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>Instagram</Text>
                <Text style={styles.infoItemSubtitle}>@padrinho.app</Text>
              </View>
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path d="M4.26683 12L3.3335 11.0667L9.7335 4.66668H4.00016V3.33334H12.0002V11.3333H10.6668V5.60001L4.26683 12Z" fill="#151515"/>
              </Svg>
            </TouchableOpacity>

            {/* Site */}
            <TouchableOpacity 
              style={styles.infoItem}
              onPress={() => WebBrowser.openBrowserAsync('https://www.padrinho.app/', {
                dismissButtonStyle: 'close',
                presentationStyle: WebBrowser.WebBrowserPresentationStyle.CURRENT_CONTEXT,
                controlsColor: '#7E7D75',
                toolbarColor: '#FFFFFF',
              })}
            >
              <View style={styles.infoItemIcon}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <Path fillRule="evenodd" clipRule="evenodd" d="M12 3C16.9709 3 21 7.02908 21 12C21 16.9709 16.9709 21 12 21C7.02908 21 3 16.9709 3 12C3 7.02908 7.02908 3 12 3Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path fillRule="evenodd" clipRule="evenodd" d="M11.9994 3C13.85 3 15.3493 7.02908 15.3493 12C15.3493 16.9709 13.85 21 11.9994 21C10.1488 21 8.64941 16.9709 8.64941 12C8.64941 7.02908 10.1488 3 11.9994 3Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M3 11.9982H21" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('padrinho.website')}</Text>
                <Text style={styles.infoItemSubtitle}>www.padrinho.app</Text>
              </View>
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path d="M4.26683 12L3.3335 11.0667L9.7335 4.66668H4.00016V3.33334H12.0002V11.3333H10.6668V5.60001L4.26683 12Z" fill="#151515"/>
              </Svg>
            </TouchableOpacity>

            {/* Políticas */}
            <TouchableOpacity 
              style={styles.infoItem}
              onPress={() => WebBrowser.openBrowserAsync('https://www.padrinho.app/termos', {
                dismissButtonStyle: 'close',
                presentationStyle: WebBrowser.WebBrowserPresentationStyle.CURRENT_CONTEXT,
                controlsColor: '#7E7D75',
                toolbarColor: '#FFFFFF',
              })}
            >
              <View style={styles.infoItemIcon}>
                <Svg width="20" height="22" viewBox="0 0 20 22" fill="none">
                  <Path fillRule="evenodd" clipRule="evenodd" d="M9.98457 20.606C9.98457 20.606 17.6566 18.283 17.6566 11.879C17.6566 5.474 17.9346 4.974 17.3196 4.358C16.7036 3.742 10.9906 1.75 9.98457 1.75C8.97857 1.75 3.26557 3.742 2.65057 4.358C2.03457 4.974 2.31257 5.474 2.31257 11.879C2.31257 18.283 9.98457 20.606 9.98457 20.606Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M7.38574 10.8746L9.27774 12.7696L13.1757 8.86963" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('padrinho.privacy')}</Text>
              </View>
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path d="M4.26683 12L3.3335 11.0667L9.7335 4.66668H4.00016V3.33334H12.0002V11.3333H10.6668V5.60001L4.26683 12Z" fill="#151515"/>
              </Svg>
            </TouchableOpacity>

            {/* Ajuda */}
            <TouchableOpacity 
              style={styles.infoItem}
              onPress={() => WebBrowser.openBrowserAsync('https://www.padrinho.app/ajuda/home', {
                dismissButtonStyle: 'close',
                presentationStyle: WebBrowser.WebBrowserPresentationStyle.CURRENT_CONTEXT,
                controlsColor: '#7E7D75',
                toolbarColor: '#FFFFFF',
              })}
            >
              <View style={styles.infoItemIcon}>
                <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <Path d="M12 21C16.9709 21 21 16.9699 21 12C21 7.02908 16.9709 3 12 3C7.02908 3 3 7.02908 3 12C3 16.9699 7.02908 21 12 21Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path fillRule="evenodd" clipRule="evenodd" d="M16.0337 11.9995C16.0337 14.2276 14.2269 16.0335 11.9988 16.0335C9.77068 16.0335 7.96484 14.2276 7.96484 11.9995C7.96484 9.77141 9.77068 7.96558 11.9988 7.96558C14.2269 7.96558 16.0337 9.77141 16.0337 11.9995Z" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <Path d="M5.79392 18.2071L8.8977 15.1033M18.2075 18.2071L15.1037 15.1033M8.89675 8.89633L5.79297 5.79352M15.1037 8.89633L18.2075 5.79352" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              </View>
              <View style={styles.infoItemContent}>
                <Text style={styles.infoItemTitle}>{t('padrinho.help')}</Text>
              </View>
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path d="M4.26683 12L3.3335 11.0667L9.7335 4.66668H4.00016V3.33334H12.0002V11.3333H10.6668V5.60001L4.26683 12Z" fill="#151515"/>
              </Svg>
            </TouchableOpacity>
          </View>
        </View>

        {/* SLIDER Section - Carousel from menu.tsx */}
        <View style={styles.carouselSection}>
          <ScrollView
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            style={styles.carouselScroll}
            contentContainerStyle={styles.carouselContainer}
            snapToInterval={345 + 8}
            decelerationRate="fast"
            snapToAlignment="start"
          >
            {/* Community Card */}
            <TouchableOpacity
              onPress={() => WebBrowser.openBrowserAsync('https://chat.whatsapp.com/CEuzR6F7MCI648Fi90adKs', {
                dismissButtonStyle: 'close',
                presentationStyle: WebBrowser.WebBrowserPresentationStyle.CURRENT_CONTEXT,
                controlsColor: '#7E7D75',
                toolbarColor: '#FFFFFF',
              })}
            >
              <ImageBackground
                source={require('../assets/images/community.png')}
                style={styles.carouselCard}
                resizeMode="cover"
                imageStyle={{ borderRadius: 12 }}
              >
                <View style={styles.cardContent}>
                  <View style={styles.cardHeader}>
                    <View style={styles.cardIcon}>
                      <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <Path d="M17 8C17 10.7614 14.7614 13 12 13C9.23858 13 7 10.7614 7 8C7 5.23858 9.23858 3 12 3C14.7614 3 17 5.23858 17 8Z" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <Path d="M3 21C3.95728 17.9027 6.41998 17 12 17C17.58 17 20.0427 17.9027 21 21" stroke="#015480" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </Svg>
                    </View>
                    <Text style={styles.cardTag}>{t('profile.cards.community.tag')}</Text>
                  </View>
                  <Text style={styles.cardTitle}>{t('profile.cards.community.title')}</Text>
                  <Text style={styles.cardSubtitle}>{t('profile.cards.community.subtitle')}</Text>
                </View>
              </ImageBackground>
            </TouchableOpacity>

            {/* Pé na Trilha Card */}
            <TouchableOpacity
              onPress={() => WebBrowser.openBrowserAsync('https://open.spotify.com/show/2xzYogycLrTYvIye5fzQBj?si=MVyGLKWNQ9qzzdpjrqjbSw', {
                dismissButtonStyle: 'close',
                presentationStyle: WebBrowser.WebBrowserPresentationStyle.CURRENT_CONTEXT,
                controlsColor: '#7E7D75',
                toolbarColor: '#FFFFFF',
              })}
            >
              <ImageBackground
                source={require('../assets/images/podcast.png')}
                style={styles.carouselCard}
                resizeMode="cover"
                imageStyle={{ borderRadius: 12 }}
              >
                <View style={styles.cardContent}>
                  <View style={styles.cardHeader}>
                    <View style={styles.cardIcon}>
                      <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
                        <Rect x="3.5" y="7" width="2" height="10" rx="1" fill="#015480"/>
                        <Rect x="7.5" y="9" width="2" height="6" rx="1" fill="#015480"/>
                        <Rect x="11.5" y="7" width="2" height="10" rx="1" fill="#015480"/>
                        <Rect x="15.5" y="5" width="2" height="14" rx="1" fill="#015480"/>
                        <Rect x="19.5" y="9" width="2" height="6" rx="1" fill="#015480"/>
                      </Svg>
                    </View>
                    <Text style={styles.cardTag}>{t('profile.cards.podcast.tag')}</Text>
                  </View>
                  <Text style={styles.cardTitle}>{t('profile.cards.podcast.title')}</Text>
                  <Text style={styles.cardSubtitle}>{t('profile.cards.podcast.subtitle')}</Text>
                </View>
              </ImageBackground>
            </TouchableOpacity>

          </ScrollView>
        </View>

        {/* CONVIDE Section - Invite card from menu.tsx */}
        <View style={styles.conviteSection}>
          <TouchableOpacity
            style={styles.conviteCard}
            onPress={async () => {
              try {
                await Share.share({
                  message: t('profile.share.inviteMessage'),
                  title: t('profile.share.inviteTitle'),
                });
              } catch (error) {
                console.error(t('profile.share.shareError'), error);
              }
            }}
          >
            <View style={styles.conviteIcon}>
              <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <Path d="M0 10C0.00011 4.21636 5.26639 -0.396253 11.2369 0.723752C15.0001 1.42987 18.0146 4.44565 18.7207 8.20941C19.8398 14.1781 15.2281 19.4445 9.44444 19.4444C7.9252 19.4444 6.41038 19.0926 5.07543 18.4537C4.29185 18.6296 3.11121 18.9188 2.25498 19.1342C0.951631 19.4623 -0.0220025 18.3014 0.282118 16.9502C0.495583 16.0021 0.787435 14.7244 0.934311 14.0444C0.358374 12.8193 0 11.4573 0 10ZM8.20849 6.76217C7.79146 6.15202 6.93313 6.07439 6.41823 6.58825L6.29632 6.70902C4.95782 8.01316 5.98276 10.0627 7.69746 11.7741C9.41305 13.4857 11.4269 14.4593 12.7281 13.1424L12.8477 13.0239C13.3547 12.5179 13.2771 11.6594 12.6678 11.2426C12.472 11.1056 12.269 10.9601 12.0548 10.8071C11.5781 10.4772 10.9341 10.527 10.5609 10.9279L10.3428 11.1448C9.39501 10.5856 8.92623 10.0485 8.29623 9.08279L8.51235 8.84688C8.91346 8.44577 8.9630548 7.80548 8.63348 7.32845C8.48025 7.11358 8.33578 6.91135 8.20849 6.76217Z" fill="#DAD8CF"/>
              </Svg>
            </View>
            <View style={styles.conviteContent}>
              <Text style={styles.conviteTitle}>{t('profile.cards.invite.title')}</Text>
              <Text style={styles.conviteSubtitle}>{t('profile.cards.invite.subtitle')}</Text>
            </View>
          </TouchableOpacity>
        </View>

        {/* LOGOUT Section - Outside card, left aligned */}
        <View style={styles.logoutSection}>
          <TouchableOpacity style={styles.logoutItem} onPress={handleLogout}>
            <View style={styles.logoutIconContainer}>
              <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <Path d="M15.0174 7.38948V6.45648C15.0174 4.42148 13.3674 2.77148 11.3324 2.77148H6.45744C4.42344 2.77148 2.77344 4.42148 2.77344 6.45648V17.5865C2.77344 19.6215 4.42344 21.2715 6.45744 21.2715H11.3424C13.3714 21.2715 15.0174 19.6265 15.0174 17.5975V16.6545" stroke="#DE5609" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M21.8105 12.0215H9.76953" stroke="#DE5609" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M18.8828 9.10645L21.8108 12.0214L18.8828 14.9374" stroke="#DE5609" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
            </View>
            <Text style={styles.logoutText}>{t('profile.logout.title')}</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.deleteAccountItem} onPress={() => router.push('/delete-account')}>
            <Text style={styles.deleteText}>{t('profile.deleteAccount.label')}</Text>
          </TouchableOpacity>
          
          <Text style={styles.versionText}>v {Constants.expoConfig?.version || '1.0.0'}</Text>
        </View>
      </ScrollView>

      {/* Toast Feedback */}
      <Toast
        visible={toastVisible}
        type={toastType}
        message={toastMessage}
        onHide={hideToast}
        duration={4000}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  header: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    backgroundColor: '#F9F8F3',
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  closeButton: {
    padding: 4,
  },
  centerContent: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  loadingText: {
    color: '#151515',
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
  },
  errorTitle: {
    color: '#151515',
    fontSize: 20,
    fontFamily: 'Inter_700Bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  errorText: {
    color: '#757C85',
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
  },
  errorActions: {
    flexDirection: 'column',
    gap: 12,
    width: '100%',
    maxWidth: 280,
  },
  retryButton: {
    backgroundColor: '#7E7D75',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontFamily: 'Inter_700Bold',
  },
  clearErrorButton: {
    backgroundColor: 'transparent',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#7E7D75',
  },
  clearErrorButtonText: {
    color: '#7E7D75',
    fontSize: 16,
    fontFamily: 'Inter_700Bold',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  profileHeaderSection: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 24,
    gap: 24,
  },
  avatarContainer: {
    position: 'relative',
  },
  avatarTouchable: {
    opacity: 1,
  },
  editButtonDisabled: {
    opacity: 0.6,
  },
  editButton: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#7E7D75',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 4,
    borderColor: '#F9F8F3',
  },
  plusBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 28,
    height: 28,
    borderRadius: 15,
    backgroundColor: '#669AB7',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 4,
    borderColor: '#F9F8F3',
  },
  greetingTextContainer: {
    alignItems: 'center',
  },
  greetingHello: {
    fontSize: 32,
    fontFamily: 'Sentient-Regular',
    color: '#A19F97',
    lineHeight: 36,
  },
  greetingName: {
    fontSize: 32,
    fontFamily: 'Sentient-Bold',
    color: '#151515',
    lineHeight: 36,
  },
  section: {
    paddingHorizontal: 24,
    paddingVertical: 8,
    paddingBottom: 16,
  },
  sectionTitleNew: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#7E7D75',
    lineHeight: 14,
    marginBottom: 12,
  },
  infoCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    paddingVertical: 4,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 16,
  },
  infoItemIcon: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  infoItemContent: {
    flex: 1,
    gap: 0,
  },
  infoItemTitle: {
    fontSize: 14,
    fontFamily: 'Inter_600SemiBold',
    color: '#151515',
    lineHeight: 19.6,
  },
  infoItemSubtitle: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#757C85',
    lineHeight: 19.6,
  },
  infoDivider: {
    height: 1,
    backgroundColor: '#F0EEE4',
    marginHorizontal: 20,
  },
  separator: {
    height: 1,
    backgroundColor: '#F0EEE4',
    marginHorizontal: 16,
  },
  infoItemDisabled: {
    opacity: 0.45,
  },
  infoItemTitleDisabled: {
    color: '#A19F97',
  },
  carouselSection: {
    paddingHorizontal: 24,
    paddingTop: 0,
    paddingBottom: 16,
    gap: 16,
  },
  carouselScroll: {
    marginHorizontal: -24,
  },
  carouselContainer: {
    paddingLeft: 24,
    paddingRight: 24,
  },
  carouselCard: {
    width: 345,
    height: 200,
    borderRadius: 12,
    overflow: 'hidden',
    marginRight: 8,
  },
  cardContent: {
    flex: 1,
    padding: 24,
    gap: 12,
    maxWidth: '65%',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  cardIcon: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardTag: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#015480',
    flex: 1,
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 22.4,
    flex: 1,
  },
  cardSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#7E7D75',
  },
  conviteSection: {
    paddingHorizontal: 24,
    paddingBottom: 16,
  },
  conviteCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#F0EEE4',
    borderRadius: 12,
    gap: 16,
    height: 72,
  },
  conviteIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#3D3C38',
    justifyContent: 'center',
    alignItems: 'center',
  },
  conviteContent: {
    flex: 1,
    gap: 4,
    justifyContent: 'center',
  },
  conviteTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  conviteSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#757C85',
    lineHeight: 12,
  },
  logoutSection: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 24,
    gap: 16,
    alignItems: 'flex-start',
  },
  logoutItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingVertical: 12,
    width: '100%',
  },
  logoutIconContainer: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  deleteAccountItem: {
    paddingVertical: 12,
    width: '100%',
  },
  logoutText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#DE5609',
    lineHeight: 19.6,
    flex: 1,
  },
  deleteText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#7E7D75',
    lineHeight: 19.6,
  },
  versionText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#7E7D75',
    lineHeight: 19.6,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardsSection: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 24,
    gap: 12,
  },
  upgradeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingVertical: 16,
    paddingLeft: 16,
    paddingRight: 24,
    backgroundColor: '#669AB7',
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  cardBackgroundContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  upgradeIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(1, 84, 128, 0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  upgradeContent: {
    flex: 1,
    gap: 4,
  },
  upgradeTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#FFFFFF',
    lineHeight: 19.6,
  },
  upgradeSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#FFFFFF',
    lineHeight: 12,
  },
  csatCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#F0EEE4',
    borderRadius: 12,
    gap: 16,
    height: 72,
  },
  csatIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#3D3C38',
    justifyContent: 'center',
    alignItems: 'center',
  },
  csatContent: {
    flex: 1,
    gap: 4,
  },
  csatTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  csatSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#757C85',
    lineHeight: 12,
  },
  sponsorSection: {
    paddingHorizontal: 24,
    paddingVertical: 8,
    paddingBottom: 16,
  },
  findSponsorCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    gap: 16,
    marginBottom: 12,
  },
  sponsorCardIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#7E7D75',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sponsorCardContent: {
    flex: 1,
    gap: 4,
  },
  sponsorCardTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  sponsorCardSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#757C85',
    lineHeight: 12,
  },
  scheduledSponsorCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    padding: 16,
    gap: 16,
  },
});